import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { CheckCheck, Sparkles, MessageCircle, AtSign, Heart, UserPlus, Repeat2, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useNotifications } from '../../hooks/useNotifications';

/**
 * NotificationsView - AI-curated intelligence briefing
 * Now uses real data from useNotifications hook
 */
export default function NotificationsView() {
  const [userId, setUserId] = useState(null);

  // Get current user on mount
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id);
    });
  }, []);

  // Use real notifications from database
  const { notifications: dbNotifications, loading, markAsRead, markAllAsRead } = useNotifications(userId || '');

  // Map DB notifications to component format
  const notifications = dbNotifications.map(n => ({
    id: n.id,
    type: n.type || 'message',
    from: n.sender?.display_name || n.sender?.username || 'Someone',
    timestamp: new Date(n.created_at),
    unread: !n.read,
    priority: !n.read ? 'high' : 'normal',
    space: n.data?.space_name || null,
    text: n.title || n.content || n.message
  }));

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleArchive = (id) => {
    markAsRead(id);
  };

  const highPriority = notifications.filter(n => n.priority === 'high');
  const recentActivity = notifications.filter(n => n.priority === 'normal');

  // Show loading state
  if (loading && !userId) {
    return (
      <div className="h-screen w-screen bg-gjyl-darker flex items-center justify-center">
        <div className="text-gray-400">Loading notifications...</div>
      </div>
    );
  }

  return (
    <motion.div
      className="h-screen w-screen bg-gjyl-darker overflow-y-auto pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gjyl-dark/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-6 pt-12 pb-4">
          <h1 className="text-3xl font-bold text-white">Intel</h1>
          <motion.button
            className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-sm"
            onClick={handleMarkAllRead}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <CheckCheck size={16} />
            <span>Mark all read</span>
          </motion.button>
        </div>
      </div>

      {/* Velvet Briefing - AI Summary */}
      <div className="px-6 py-6">
        <motion.div
          className="relative rounded-2xl p-5 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Animated shimmer */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
            animate={{
              x: ['-100%', '200%']
            }}
            transition={{
              repeat: Infinity,
              duration: 3,
              ease: 'linear'
            }}
          />

          {/* Content */}
          <div className="relative z-10">
            <div className="flex items-center space-x-2 mb-3">
              <motion.div
                animate={{
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear'
                }}
              >
                <Sparkles className="w-5 h-5 text-purple-400" />
              </motion.div>
              <h2 className="text-white font-semibold">Velvet Briefing</h2>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Good morning. You missed <strong className="text-white">12 messages</strong> in{' '}
              <strong className="text-purple-400">Cyberpunk Chill</strong> and{' '}
              <strong className="text-white">3 mentions</strong> in{' '}
              <strong className="text-purple-400">Gaming Hub</strong>.{' '}
              <strong className="text-white">Alice</strong> sent you a DM.{' '}
              You have <strong className="text-white">1 new follower</strong>.
            </p>
          </div>
        </motion.div>
      </div>

      {/* High Priority Section */}
      {highPriority.length > 0 && (
        <div className="px-6 py-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            High Priority
          </h3>
          <div className="space-y-2">
            {highPriority.map((notification, index) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                index={index + 1}
                onArchive={handleArchive}
                isHighPriority
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity Section */}
      {recentActivity.length > 0 && (
        <div className="px-6 py-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Recent Activity
          </h3>
          <div className="space-y-2">
            {recentActivity.map((notification, index) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                index={highPriority.length + index + 1}
                onArchive={handleArchive}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {notifications.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center h-64 text-center px-8"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
            <CheckCheck className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-white font-semibold text-lg mb-2">All caught up!</h3>
          <p className="text-gray-400 text-sm">You're up to date on all notifications</p>
        </motion.div>
      )}
    </motion.div>
  );
}

/**
 * NotificationItem - Individual notification with swipe-to-archive
 */
function NotificationItem({ notification, index, onArchive, isHighPriority = false }) {
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-100, 0], [0, 1]);
  const backgroundColor = useTransform(
    x,
    [-100, -50, 0],
    ['rgb(239, 68, 68)', 'rgb(239, 68, 68, 0.5)', 'transparent']
  );

  const handleDragEnd = (event, info) => {
    if (info.offset.x < -100) {
      onArchive(notification.id);
    }
  };

  return (
    <motion.div
      className="relative overflow-hidden rounded-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ delay: index * 0.05 }}
      style={{ backgroundColor }}
    >
      {/* Archive hint */}
      <motion.div
        className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center space-x-2"
        style={{ opacity: useTransform(x, [-100, 0], [1, 0]) }}
      >
        <X className="w-5 h-5 text-white" />
        <span className="text-white text-sm font-medium">Archive</span>
      </motion.div>

      {/* Notification content */}
      <motion.div
        className={`relative z-10 flex items-center space-x-3 p-4 rounded-xl cursor-pointer ${
          isHighPriority ? 'bg-white/5' : 'bg-zinc-900/50'
        } hover:bg-white/10 transition-colors`}
        drag="x"
        dragConstraints={{ left: -120, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        style={{ x, opacity }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Icon/Avatar */}
        <div className="relative flex-shrink-0">
          {notification.type === 'dm' || notification.type === 'mention' ? (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
              {notification.from.charAt(0)}
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-gray-400">
              {getNotificationIcon(notification.type)}
            </div>
          )}
          {notification.unread && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-gjyl-accent rounded-full border-2 border-gjyl-dark" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm">
            {formatNotificationText(notification)}
          </p>
          <p className="text-gray-400 text-xs mt-0.5">
            {formatTime(notification.timestamp)}
          </p>
        </div>

        {/* Type icon (right side for high priority) */}
        {isHighPriority && (
          <div className="flex-shrink-0">
            {notification.type === 'dm' ? (
              <MessageCircle className="w-5 h-5 text-purple-400" />
            ) : (
              <AtSign className="w-5 h-5 text-purple-400" />
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/**
 * Get icon for notification type
 */
function getNotificationIcon(type) {
  switch (type) {
    case 'like':
      return <Heart className="w-5 h-5" fill="currentColor" />;
    case 'follow':
      return <UserPlus className="w-5 h-5" />;
    case 'repost':
      return <Repeat2 className="w-5 h-5" />;
    default:
      return <Sparkles className="w-5 h-5" />;
  }
}

/**
 * Format notification text
 */
function formatNotificationText(notification) {
  switch (notification.type) {
    case 'dm':
      return (
        <>
          <strong>{notification.from}</strong> sent you a message
        </>
      );
    case 'mention':
      return (
        <>
          <strong>{notification.from}</strong> mentioned you in{' '}
          <strong className="text-purple-400">{notification.space}</strong>
        </>
      );
    case 'like':
      if (notification.count > 1) {
        return (
          <>
            <strong>{notification.from}</strong> and{' '}
            <strong>{notification.count - 1} others</strong> liked your video
          </>
        );
      }
      return (
        <>
          <strong>{notification.from}</strong> liked your video
        </>
      );
    case 'follow':
      return (
        <>
          <strong>{notification.from}</strong> started following you
        </>
      );
    case 'repost':
      return (
        <>
          <strong>{notification.from}</strong> reposted your video
        </>
      );
    default:
      return notification.text;
  }
}

/**
 * Format timestamp
 */
function formatTime(timestamp) {
  const now = new Date();
  const time = new Date(timestamp);
  const diff = now - time;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Mock data removed - now using real data from useNotifications hook
